import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormValues } from 'redux-form';
import get from 'lodash-es/get';

import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { Dictionary, DictionaryEntry } from 'app/services/dict.service';
import { QuestionInterface } from 'app/shared/interfaces/Question.interface';
import {
  parseCarDoorsDictionary,
  parseCarVersionDictionary,
  parseProductionYearDictionary,
  setHorsePowerDictionary,
} from './helpers/fetchDictionary';
import {
  ShortPathDataActions,
  selectCepikData,
  selectCepikMergeData,
} from 'app/store/data/shortPathData';
import { CalculationDataByKeysInterface } from 'app/shared/interfaces/CalculationDataByKeys.interface';
import { getCapitalized } from 'app/shared/helpers/getCapitalized';

export function useCepikCarData(
  question: QuestionInterface,
  formName: FormsEnum
) {
  const [cepikQuestion, setCepikQuestion] = useState(question);
  const dispatch = useDispatch();

  const isCepikModal = formName === FormsEnum.CompareCepikDataForm;
  const cepikCarData = useSelector(
    isCepikModal ? selectCepikMergeData : selectCepikData
  );

  const calculationFormValues = useSelector(
    getFormValues(FormsEnum.CalculationForm)
  ) as CalculationDataByKeysInterface;

  const getQuestionFormValue = (activeQuestionKey: FieldsEnum) =>
    get(calculationFormValues, activeQuestionKey);

  const openCepikDataRejectionModal = () => {
    dispatch(ShortPathDataActions.setIsCepikDataRejectionModalOpened(true));
  };

  const parseIntIfPossible = (value: string) => {
    var integerValue = parseInt(value);

    return !isNaN(integerValue) && value === '' + integerValue
      ? integerValue
      : value;
  };

  const getCepikDictionary = (
    activeQuestionKey: FieldsEnum,
    dictionary: Dictionary
  ) => {
    if (!cepikCarData) return [];

    const config: Partial<{ [key in FieldsEnum]: Dictionary }> = {
      [FieldsEnum.CarProductionYear]: cepikCarData.productionYear,
      [FieldsEnum.CarMake]: cepikCarData.make,
      [FieldsEnum.CarModel]: cepikCarData.model,
      [FieldsEnum.CarFuelType]: cepikCarData.fuelType,
      [FieldsEnum.CarEngineCapacity]: cepikCarData.engineVolume,
      [FieldsEnum.CarDoorsCount]: cepikCarData.doorsCount,
      [FieldsEnum.CarVersionEt]: cepikCarData.versionEt,
      [FieldsEnum.CarHorsePower]: cepikCarData.versionEt,
    };

    let cepikDictionary = config[activeQuestionKey]?.filter((entry) =>
      dictionary.find((item) => String(item.key) === String(entry.key))
    );

    if (isCepikModal) {
      if (!cepikDictionary) cepikDictionary = [];

      const activeQuestionFormValue = getQuestionFormValue(activeQuestionKey);
      let ownDictionary: DictionaryEntry[] = [];
      let fieldCepikDictionary: DictionaryEntry[] = [];

      switch (activeQuestionKey) {
        case FieldsEnum.CarVersionEt:
          fieldCepikDictionary = parseCarVersionDictionary(cepikDictionary);

          ownDictionary = dictionary.filter(
            (entry) =>
              !fieldCepikDictionary.find((item) => item.key === entry.key)
          );
          break;
        case FieldsEnum.CarProductionYear:
          fieldCepikDictionary = parseProductionYearDictionary(cepikDictionary);

          ownDictionary = dictionary.filter(
            (entry) =>
              !fieldCepikDictionary.find((item) => item.key === entry.key)
          );
          break;
        case FieldsEnum.CarDoorsCount:
          fieldCepikDictionary = parseCarDoorsDictionary(cepikDictionary);

          ownDictionary = dictionary.filter(
            (entry) =>
              !fieldCepikDictionary.find((item) => item.key === entry.key)
          );
          break;
        case FieldsEnum.CarMake:
          fieldCepikDictionary = cepikDictionary;

          ownDictionary = dictionary.filter((entry) => {
            return !fieldCepikDictionary.find(
              (item) => item.key === entry.key || entry.group === 'popular'
            );
          });
          break;
        case FieldsEnum.CarModel:
        case FieldsEnum.CarFuelType:
        case FieldsEnum.CarEngineCapacity:
        case FieldsEnum.CarModelIe:
        case FieldsEnum.CarVersionIe:
          fieldCepikDictionary = cepikDictionary;

          ownDictionary = dictionary.filter((entry) => {
            return !fieldCepikDictionary.find((item) => item.key === entry.key);
          });
          break;
        case FieldsEnum.CarHorsePower:
          dictionary = setHorsePowerDictionary(dictionary);

          fieldCepikDictionary = setHorsePowerDictionary(
            cepikDictionary
          ).filter((item) =>
            cepikCarData.powerKw.some((entry) => entry.key === item.key)
          );

          ownDictionary = dictionary.filter(
            (item) =>
              !fieldCepikDictionary.find((entry) => entry.key === item.key)
          );
      }

      const key = activeQuestionKey.split('.').slice(-1)[0];

      const selectedFormValue = dictionary.find(
        (item) =>
          item.key ===
          (activeQuestionKey === FieldsEnum.CarProductionYear
            ? parseIntIfPossible(activeQuestionFormValue)
            : activeQuestionFormValue)
      );

      const cepikGroup: Dictionary = fieldCepikDictionary.map(
        (entry: DictionaryEntry) => ({
          ...entry,
          group: 'cepik',
          value:
            dictionary.find((el) => el.key === entry.key)?.value || entry.value,
          valueShort:
            dictionary.find((el) => el.key === entry.key)?.valueShort ||
            entry.valueShort,
        })
      );

      if (selectedFormValue) {
        cepikGroup.unshift({
          ...selectedFormValue,
          group: 'cepik-form',
        });

        ownDictionary = ownDictionary.filter(
          (entry) => selectedFormValue.key !== entry.key
        );
      }

      if (!cepikGroup.length) {
        return [];
      }

      const restGroup = ownDictionary.map((entry: DictionaryEntry) => ({
        ...entry,
        group: 'dictionary',
      }));

      setCepikQuestion({
        ...question,
        answerGroups: [
          {
            key: `cepik${getCapitalized(key)}`,
            groupName: `cepik${getCapitalized(key)}`,
            label: '',
            subLabel: '',
          },
          {
            key,
            groupName: key,
            label: '',
            subLabel: '',
          },
        ],
      });

      return [...cepikGroup, ...restGroup];
    }

    if (cepikDictionary && cepikDictionary.length > 0) {
      if (activeQuestionKey === FieldsEnum.CarDoorsCount) {
        return parseCarDoorsDictionary(cepikDictionary);
      }

      if (activeQuestionKey === FieldsEnum.CarVersionEt) {
        const carVersionEtCepikDictionary = parseCarVersionDictionary(
          cepikDictionary
        );

        const ownDictionary = dictionary.filter(
          (entry) =>
            !carVersionEtCepikDictionary.find((item) => item.key === entry.key)
        );

        const cepikGroup = carVersionEtCepikDictionary.map(
          (entry: DictionaryEntry) => ({
            ...entry,
            group: 'cepik',
          })
        );

        const restGroup = ownDictionary.map((entry: DictionaryEntry) => ({
          ...entry,
          group: 'dictionary',
        }));

        setCepikQuestion({
          ...question,
          answerGroups: [
            {
              key: 'cepikVersionEt',
              groupName: 'cepikVersionEt',
              label: '',
              subLabel: '',
            },
            {
              key: 'versionEt',
              groupName: 'versionEt',
              label: '',
              subLabel: '',
            },
          ],
        });

        return [...cepikGroup, ...restGroup];
      }

      if (activeQuestionKey === FieldsEnum.CarHorsePower) {
        return setHorsePowerDictionary(cepikDictionary);
      }

      return cepikDictionary;
    }

    return [];
  };

  return {
    cepikCarData,
    cepikQuestion,
    getCepikDictionary,
    openCepikDataRejectionModal,
  };
}
